"use client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
import { useState, useEffect } from "react";
import Link from "next/link";
import { DarkThemeToggle } from "flowbite-react";
import {
  FaFacebookF,
  FaXTwitter,
  FaYoutube,
  FaLinkedinIn,
  FaTiktok,
  FaInstagram,
} from "react-icons/fa6";

// ✅ Define the Article type
type Article = {
  id: number;
  title: string;
};

const TrendingBar = () => {
  const [headlines, setHeadlines] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [currentDate, setCurrentDate] = useState("");

  // ✅ Fetch articles from API
  useEffect(() => {
    const fetchHeadlines = async () => {
      try {
        const response = await fetch("/api/articles?pageSize=5");
        const json = await response.json();
        if (!json.success) throw new Error("Failed to fetch articles");
        const titles = json.articles.map((article: Article) => article.title);
        setHeadlines(titles);
      } catch (error) {
        console.error("Error fetching articles:", error);
      }
    };
    fetchHeadlines();
  }, []);

  // ✅ Update the date
  useEffect(() => {
    setCurrentDate(
      new Date().toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    );
  }, []);

  // ✅ Auto-scroll every 5s
  useEffect(() => {
    if (headlines.length > 1) {
      const interval = setInterval(() => {
        setIndex((prevIndex) => (prevIndex + 1) % headlines.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [headlines]);

  // ✅ Manual navigation
  const nextSlide = () =>
    setIndex((prevIndex) => (prevIndex + 1) % headlines.length);
  const prevSlide = () =>
    setIndex(
      (prevIndex) => (prevIndex - 1 + headlines.length) % headlines.length,
    );

  return (
    <div className="flex flex-wrap items-center justify-between border-b bg-white px-4 py-2 text-gray-700 dark:bg-gray-900 dark:text-white">
      {/* ✅ Badge Trending + Headlines */}
      <div className="flex min-w-0 flex-1 items-center space-x-2">
        <span className="bg-red-600 px-2 py-1 text-xs font-bold text-white dark:bg-red-500">
          Tendance
        </span>
        <div className="relative h-5 w-full overflow-hidden">
          {headlines.length > 0 ? (
            <p
              key={index}
              className="absolute inset-0 w-full truncate text-sm font-medium opacity-100 transition-transform duration-500 ease-in-out"
              dangerouslySetInnerHTML={{ __html: headlines[index] }}
            />
          ) : (
            <p className="text-sm text-gray-400">Chargement des tendances...</p>
          )}
        </div>
      </div>

      {/* ✅ Navigation Buttons */}
      <div className="hidden items-center md:flex">
        <button
          onClick={prevSlide}
          aria-label="Headline précédent"
          className="text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <span className="mx-4 text-sm text-gray-500 dark:text-gray-300">
          {currentDate}
        </span>
        <button
          onClick={nextSlide}
          aria-label="Headline suivant"
          className="text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>

      {/* ✅ Dark Mode Toggle */}
      <div className="ml-6 flex items-center">
        <DarkThemeToggle />
      </div>

      {/* ✅ Social Media Icons */}
      <div className="ml-6 hidden items-center gap-4 sm:flex">
        {[
          {
            href: "https://www.facebook.com/profile.php?id=100092728413759",
            icon: FaFacebookF,
            color: "text-blue-600 hover:text-blue-800 dark:text-blue-400",
          },
          {
            href: "https://x.com/maglesconcernes?t=rERA2tJwLVRQktgypfQq4Q",
            icon: FaXTwitter,
            color:
              "text-black hover:text-gray-800 dark:text-white dark:hover:text-gray-400",
          },
          {
            href: "https://www.youtube.com/@lesconcernes",
            icon: FaYoutube,
            color: "text-red-600 hover:text-red-800 dark:text-red-400",
          },
          {
            href: "https://www.linkedin.com/company/les-conc%C3%A9rnes/",
            icon: FaLinkedinIn,
            color: "text-blue-700 hover:text-blue-900 dark:text-blue-400",
          },
          {
            href: "https://tiktok.com",
            icon: FaTiktok,
            color:
              "text-black hover:text-gray-800 dark:text-white dark:hover:text-gray-400",
          },
          {
            href: "https://www.instagram.com/les_concernes/profilecard/?igsh=Y3drdzlhazh2bTB5",
            icon: FaInstagram,
            color: "text-pink-500 hover:text-pink-700 dark:text-pink-400",
          },
        ].map(({ href, icon: Icon, color }, i) => (
          <Link key={i} target="_blank" href={href} className={color}>
            <Icon className="h-4 w-4 md:h-5 md:w-5" />
          </Link>
        ))}
      </div>

      {/* ✅ Newsletter Button */}
      <Link target="_blank" href="/newsletter">
        <button className="ml-4 hidden rounded bg-blue-600 px-3 py-1 text-xs font-bold text-white hover:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-400 sm:block">
          Newsletter
        </button>
      </Link>
    </div>
  );
};

export default TrendingBar;
